/**
 * # InactivityHandler Component
 *
 * ## Overview
 * The InactivityHandler component manages user session timeouts and provides automatic logout functionality.
 *
 * ## Purpose
 * - Ensures users are automatically logged out after periods of inactivity
 * - Provides clear warnings before automatic logout
 *
 * ## How It Works
 *
 * ### Activity Detection
 * The component monitors user activity through multiple event listeners:
 * - Mouse movements (`mousemove`)
 * - Keyboard input (`keydown`)
 * - Page scrolling (`scroll`)
 * - Touch interactions (`touchstart`)
 *
 * ### Timeout Management
 * - Warning Period: 25 minutes of inactivity triggers a 5-minute warning toast
 * - Logout Period: 30 minutes of inactivity triggers automatic logout
 * - Debounced Reset: Activity detection is debounced (100ms) to prevent excessive timeout resets
 *
 * ### User Notifications
 * - Warning Toast: Appears 5 minutes before logout
 * - Logout Toast: Confirms logout action with error styling
 * - Duration: Warning shows for 10 seconds, logout confirmation for 10 seconds
 *
 * ## Time Limits
 *
 * | Event | Duration | Action |
 * |-------|----------|---------|
 * | User Activity | Any time | Resets both warning and logout timers |
 * | Inactivity Warning | 25 minutes | Shows 5-minute warning toast |
 * | Automatic Logout | 30 minutes | Logs user out and clears session |
 *
 *
 * ## Configuration
 *
 * Current timeout values (configurable):
 * ```javascript
 * const inactivityTimeout = 30 * 60 * 1000; // 30 minutes
 * const warningTimeout = 25 * 60 * 1000;    // 25 minutes (5 min warning)
 * ```
 *
 * ## Usage
 *
 * This component is included in the App.tsx to ensure
 * session management is active for all authenticated users. It automatically
 * activates when `isAuthenticated` is true and deactivates on logout.
 *
 * ## Testing Notes
 * For development/testing purposes, shorter timeouts can be enabled:
 * ```javascript
 * // const inactivityTimeout = 1 * 60 * 1000; // 1 minute
 * // const warningTimeout = 0.5 * 60 * 1000; // 30 seconds
 * ```
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { debounce } from "lodash";

// store
import useAuthStore from "store/useAuthStore";

const inactivityTimeout = 30 * 60 * 1000; // 30 minutes
const warningTimeout = 25 * 60 * 1000; // 5 minutes before logout
// const inactivityTimeout = 1 * 60 * 1000; // 1 minute
// const warningTimeout = 0.5 * 60 * 1000; // 30 seconds

let logoutTimeoutId: NodeJS.Timeout | null = null;
let warningTimeoutId: NodeJS.Timeout | null = null;

export default function InactivityHandler() {
  const { isAuthenticated, resetAuthentication, logoutOkta } = useAuthStore();

  useEffect(() => {
    const debouncedResetTimeout = debounce(resetTimeout, 100);

    if (isAuthenticated) {
      resetTimeout();

      const events = ["mousemove", "keydown", "scroll", "touchstart"];
      events.forEach((event) =>
        window.addEventListener(event, debouncedResetTimeout)
      );

      return () => {
        events.forEach((event) =>
          window.removeEventListener(event, debouncedResetTimeout)
        );

        if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
        if (warningTimeoutId) clearTimeout(warningTimeoutId);

        debouncedResetTimeout.cancel();
      };
    }
  }, [isAuthenticated]);

  const resetTimeout = () => {
    if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
    if (warningTimeoutId) clearTimeout(warningTimeoutId);

    warningTimeoutId = setTimeout(() => {
      toast.warning(
        "Your session will log out in 5 minutes due to inactivity. Please save and exit.",
        {
          position: "bottom-left",
          duration: 10 * 1000, // 10 seconds
        }
      );
    }, warningTimeout);

    logoutTimeoutId = setTimeout(() => {
      toast.dismiss();
      toast.error("You have been logged out due to inactivity.", {
        duration: 10 * 1000,
        position: "bottom-left",
      });

      resetAuthentication();
      // logoutOkta();
    }, inactivityTimeout);
  };

  return null;
}
